import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Descriptions,
  message,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  EditOutlined,
  ReloadOutlined,
  SendOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { commercialProposalApi, downloadProposalPdf } from "../../api/commercialProposalApi";
import {
  CommercialProposalStatus,
  ICommercialProposal,
} from "../../types/commercialProposal.type";

const STATUS_COLORS: Record<CommercialProposalStatus, string> = {
  [CommercialProposalStatus.Draft]: "default",
  [CommercialProposalStatus.Created]: "green",
  [CommercialProposalStatus.Sent]: "blue",
  [CommercialProposalStatus.Completed]: "purple",
  [CommercialProposalStatus.Expired]: "red",
};

function isProposalOverdue(status: CommercialProposalStatus, dueDate?: string): boolean {
  if (!dueDate) return false;
  if (
    [
      CommercialProposalStatus.Draft,
      CommercialProposalStatus.Completed,
      CommercialProposalStatus.Expired,
    ].includes(status)
  )
    return false;
  return new Date(dueDate).getTime() < Date.now();
}

export default function CommercialProposalViewPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [proposal, setProposal] = useState<ICommercialProposal | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [htmlLoading, setHtmlLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setHtmlLoading(true);
    try {
      const { data } = await commercialProposalApi.getById(id);
      setProposal(data.proposal);
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Ошибка при загрузке КП");
      navigate("/commercial-proposals");
      return;
    } finally {
      setLoading(false);
    }

    try {
      const { data } = await commercialProposalApi.getHtml(id);
      setHtml(data.html);
    } catch {
      setHtml(null);
      message.warning("Не удалось загрузить предпросмотр документа");
    } finally {
      setHtmlLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleDownload = async () => {
    if (!proposal) return;
    setDownloading(true);
    try {
      await downloadProposalPdf(proposal._id, proposal.proposalNumber);
    } finally {
      setDownloading(false);
    }
  };

  const handleStatusChange = async (status: CommercialProposalStatus) => {
    if (!proposal) return;
    setStatusUpdating(true);
    try {
      await commercialProposalApi.updateStatus(proposal._id, status);
      message.success("Статус обновлён");
      fetchAll();
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Ошибка при обновлении статуса");
    } finally {
      setStatusUpdating(false);
    }
  };

  if (loading || !proposal) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  const number = `КП-${String(proposal.proposalNumber).padStart(4, "0")}${
    proposal.version > 1 ? `/${proposal.version}` : ""
  }`;
  const isEditable = [CommercialProposalStatus.Draft, CommercialProposalStatus.Created].includes(
    proposal.status
  );
  const formatMoney = (value: number) =>
    `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(value)} ${proposal.currency}`;

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/commercial-proposals")}>
            Назад
          </Button>
          <h2 style={{ margin: 0 }}>
            {number} <Tag color={STATUS_COLORS[proposal.status]}>{proposal.status}</Tag>
          </h2>
        </Space>
        <Space wrap>
          {proposal.status === CommercialProposalStatus.Draft && (
            <Popconfirm
              title="Перевести черновик в «Создано»?"
              okText="Да"
              cancelText="Отмена"
              onConfirm={() => handleStatusChange(CommercialProposalStatus.Created)}
            >
              <Button icon={<CheckOutlined />} loading={statusUpdating}>
                Завершить черновик
              </Button>
            </Popconfirm>
          )}
          {proposal.status === CommercialProposalStatus.Created && (
            <Popconfirm
              title="Отметить КП как отправленное? После этого его нельзя будет изменить."
              okText="Да"
              cancelText="Отмена"
              onConfirm={() => handleStatusChange(CommercialProposalStatus.Sent)}
            >
              <Button icon={<SendOutlined />} loading={statusUpdating}>
                Отметить отправленным
              </Button>
            </Popconfirm>
          )}
          {(proposal.status === CommercialProposalStatus.Created ||
            proposal.status === CommercialProposalStatus.Sent) && (
            <Popconfirm
              title="Отметить КП как выполненное? Дедлайн перестанет считаться просроченным."
              okText="Да"
              cancelText="Отмена"
              onConfirm={() => handleStatusChange(CommercialProposalStatus.Completed)}
            >
              <Button icon={<CheckOutlined />} loading={statusUpdating}>
                Отметить выполненным
              </Button>
            </Popconfirm>
          )}
          {isEditable && (
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/commercial-proposals/${proposal._id}/edit`)}
            >
              Редактировать
            </Button>
          )}
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={handleDownload}
          >
            Скачать PDF
          </Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
          <Descriptions.Item label="Клиент">{proposal.clientName}</Descriptions.Item>
          <Descriptions.Item label="Телефон клиента">
            {proposal.clientPhone || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Менеджер">{proposal.ownerName}</Descriptions.Item>
          <Descriptions.Item label="Телефон менеджера">
            {proposal.ownerPhone || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Дата создания">
            {dayjs(proposal.createdAt).format("DD.MM.YYYY HH:mm")}
          </Descriptions.Item>
          <Descriptions.Item label="Срок действия">
            {proposal.validityDays} дн. — до {dayjs(proposal.validUntil).format("DD.MM.YYYY")}
          </Descriptions.Item>
          <Descriptions.Item label="Срок выполнения">
            {proposal.leadTimeDays > 0 || proposal.dueDate ? (
              <span
                style={{
                  color: isProposalOverdue(proposal.status, proposal.dueDate)
                    ? "#cf1322"
                    : undefined,
                  fontWeight: isProposalOverdue(proposal.status, proposal.dueDate) ? 600 : undefined,
                }}
              >
                {proposal.leadTimeDays} дн.
                {proposal.dueDate && ` — до ${dayjs(proposal.dueDate).format("DD.MM.YYYY")}`}
                {proposal.leadTimeManual && " (скорректирован вручную)"}
                {isProposalOverdue(proposal.status, proposal.dueDate) && " — просрочено ⚠"}
              </span>
            ) : (
              "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Позиций">{proposal.items.length}</Descriptions.Item>
          <Descriptions.Item label="Итого">
            <b>{formatMoney(proposal.totalAmount)}</b>
            {proposal.totalDiscountAmount > 0 && (
              <span style={{ color: "#15803d", marginLeft: 8 }}>
                (скидка {formatMoney(proposal.totalDiscountAmount)})
              </span>
            )}
          </Descriptions.Item>
          {proposal.updatedByName && (
            <Descriptions.Item label="Изменил">{proposal.updatedByName}</Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card
        title="Предпросмотр документа"
        extra={
          <Tooltip title="Обновить предпросмотр">
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchAll} />
          </Tooltip>
        }
        bodyStyle={{ padding: 0, background: "#e8ede9" }}
      >
        {htmlLoading ? (
          <div style={{ padding: 48, textAlign: "center" }}>
            <Spin />
          </div>
        ) : html ? (
          <iframe
            title="Предпросмотр КП"
            srcDoc={html}
            style={{ width: "100%", height: "78vh", border: "none", display: "block" }}
          />
        ) : (
          <div style={{ padding: 48, textAlign: "center", color: "#999" }}>
            Предпросмотр недоступен. Попробуйте скачать PDF.
          </div>
        )}
      </Card>
    </div>
  );
}
